import React, {
  useLayoutEffect,
  useRef,
  useState,
  Children,
  cloneElement,
} from 'react';

import { unpackRef } from '../../utils';

/**
 * @description Hook for inferring child component dimensions (without displaying it)
 */
export const useDimensions = (
  children: React.ReactElement
): {
  Content: () => React.JSX.Element;
  dimensions: { width?: number; height?: number; borderRadius?: string };
} => {
  const [isDisplayChildren, setIsDisplayChildren] = useState(true);
  const ref = useRef<HTMLElement>(null);
  const [height, setHeight] = useState<number>();
  const [width, setWidth] = useState<number>();
  const [borderRadius, setBorderRadius] = useState<string>();

  useLayoutEffect(() => {
    const DOMNode = unpackRef(ref);
    if (DOMNode) {
      const styles = getComputedStyle(DOMNode);
      setBorderRadius(styles.borderRadius);
      setHeight(DOMNode.offsetHeight);
      setWidth(DOMNode.offsetWidth);
      setIsDisplayChildren(false);
    }
  }, []);

  // It is relatively important that we provide style overrides for container
  // only once we're no longer rendering children within said container. This
  // helps us to prevent overflowing of parent container.
  const isStyleOverride = !isDisplayChildren;

  const Content = () => {
    return (
      <>
        {isDisplayChildren && children
          ? Children.map(children, (child) =>
              cloneElement(child, {
                ref: ref,
                ...child.props,
              })
            )
          : null}
      </>
    );
  };

  return {
    Content,
    dimensions: isStyleOverride ? { width, height, borderRadius } : {},
  };
};
